import { defineStore, acceptHMRUpdate } from 'pinia'
import baseAxios from '@app/axios'

export const useUserStore = defineStore({
  id: 'user',
  state: () => ({
    user: null,
    isInitialized: false,
    isUserUpdated: false,
  }),
  getters: {
    /**
     * This getter checks if the current user is an administrator.
     * Otherwise, it returns false.
     * @returns {boolean} - Whether the current user is an administrator.
     */
    isAdministrator() {
      return this.user?.user_role_type.user_role_type_name === 'administrator' ||
        this.user?.user_role_type.user_role_type_name === 'super_admin'
    },
    /**
     * This getter checks if the current user is an administrator.
     * Otherwise, it returns false.
     * @returns {boolean} - Whether the current user is an administrator.
     */
    isLead() {
      return this.user?.user_role_type.user_role_type_name.includes(
        'lead_officer'
      )
    },
    /**
     * This getter checks if the current user is an officer.
     * Otherwise, it returns false.
     * @returns {boolean} - Whether the current user is an officer.
     */
    isOfficer() {
      return this.user?.user_role_type.user_role_type_name.includes('officer')
    },
  },

  actions: {
    /**
     * Fetches the current user's data from the server.
     * Regardless of the request's success, it sets the isInitialized state to true.
     * @returns {Promise<void>}
     */
    async fetchUser() {
      try {
        const { data } = await baseAxios.get('/users/me')
        this.user = data
      } finally {
        this.isInitialized = true
      }
    },

    /**
     * Signs in the user.
     * @param {object} payload - The payload for the request.
     * @returns {Promise<object>} - The fetched data.
     */
    async signIn(payload) {
      const { data } = await baseAxios.post('/users/sign_in', payload)
      this.user = data
      return data
    },

    /**
     * Resets the user's password.
     * @param {object} payload - The payload for the request.
     * @returns {Promise<object>} - The fetched data.
     */
    async resetPassword(payload) {
      const { data } = await baseAxios.post('/users/reset_password', payload)
      return data
    },

    /**
     * Changes the user's password.
     * @param {object} payload - The payload for the request.
     * @returns {Promise<object>} - The fetched data.
     */
    async changePassword(payload) {
      const { data } = await baseAxios.put('/users/update_password', payload)
      this.user = null
      return data
    },

    /**
     * Logs out the user.
     * @returns {Promise<object>} - The fetched data.
     */
    async logOut() {
      const { data } = await baseAxios.delete('/users/sign_out')
      this.user = null
      return data
    },

    /**
     * Updates the user's data.
     * @param {object} payload - The payload for the request.
     * @returns {Promise<object>} - The fetched data.
     */
    async updateUser(payload) {
      const { data } = await baseAxios.patch('/users', payload)
      this.user = data
      this.isUserUpdated = true
      return data
    },

    /**
     * Unchecks the user updated state.
     * Sets the isUserUpdated state to false.
     */
    uncheckUserUpdatedState() {
      this.isUserUpdated = false
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
}
